import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});
    const [isDark, setIsDark] = useState(false);

    // Load theme preference on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        setIsDark(savedTheme === 'dark');
    }, []);

    // Toggle theme and save preference
    const toggleTheme = () => {
        const newTheme = !isDark;
        setIsDark(newTheme);
        localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
            newErrors.email = 'Invalid email address';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        setError('');

        const result = await login(email, password, rememberMe);

        if (result.success) {
            window.location.href = '/dashboard/admin';
        } else {
            setError(result.error);
            setIsLoading(false);
        }
    };

    const getStyles = () => ({
        container: {
            minHeight: '100vh',
            display: 'flex',
            fontFamily: "'Inter', 'Nunito', system-ui, sans-serif",
            transition: 'background-color 0.3s ease',
        },
        leftPanel: {
            display: 'none',
            width: '40%',
            background: isDark
                ? 'linear-gradient(135deg, #022c22 0%, #064e3b 50%, #065f46 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'background 0.3s ease',
        },
        leftContent: {
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '3rem',
            color: 'white',
            height: '100%',
        },
        title: {
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
        },
        subtitle: {
            fontSize: '1.25rem',
            marginBottom: '2rem',
            opacity: 0.9,
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '1rem',
            opacity: 0.8,
        },
        featureDot: {
            width: '8px',
            height: '8px',
            backgroundColor: isDark ? '#34d399' : '#10b981',
            borderRadius: '50%',
            marginRight: '12px',
            transition: 'background-color 0.3s ease',
        },
        rightPanel: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            background: isDark
                ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
                : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            transition: 'background 0.3s ease',
        },
        formContainer: {
            width: '100%',
            maxWidth: '28rem',
            position: 'relative',
        },
        themeToggle: {
            position: 'absolute',
            top: '-60px',
            right: '0',
            background: isDark ? '#2d2d2d' : 'white',
            border: `2px solid ${isDark ? '#444' : '#e5e7eb'}`,
            borderRadius: '50px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: isDark
                ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
        themeIcon: {
            fontSize: '1.2rem',
        },
        card: {
            backgroundColor: isDark ? '#1e1e1e' : 'white',
            borderRadius: '1rem',
            boxShadow: isDark
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '2rem',
            border: isDark ? '1px solid #333' : 'none',
            transition: 'all 0.3s ease',
        },
        logoMobile: {
            textAlign: 'center',
            marginBottom: '2rem',
        },
        logoText: {
            fontSize: '1.875rem',
            fontWeight: 'bold',
            background: isDark
                ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        header: {
            marginBottom: '2rem',
        },
        heading: {
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '0.5rem',
            transition: 'color 0.3s ease',
        },
        subheading: {
            color: isDark ? '#a0a0a0' : '#6b7280',
            transition: 'color 0.3s ease',
        },
        errorBox: {
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: isDark ? '#3d1a1a' : '#fef2f2',
            border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`,
            borderRadius: '0.5rem',
            transition: 'all 0.3s ease',
        },
        errorText: {
            color: isDark ? '#fca5a5' : '#dc2626',
            fontSize: '0.875rem',
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column',
        },
        label: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: isDark ? '#d1d5db' : '#374151',
            marginBottom: '0.5rem',
            transition: 'color 0.3s ease',
        },
        inputWrapper: {
            position: 'relative',
        },
        input: {
            width: '100%',
            padding: '0.75rem 1rem',
            border: `1px solid ${isDark ? '#404040' : '#d1d5db'}`,
            borderRadius: '0.5rem',
            fontSize: '1rem',
            transition: 'all 0.2s',
            backgroundColor: isDark ? '#2d2d2d' : '#f9fafb',
            color: isDark ? '#ffffff' : '#1f2937',
            boxSizing: 'border-box',
        },
        inputError: {
            borderColor: isDark ? '#ef4444' : '#ef4444',
        },
        passwordToggle: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: isDark ? '#9ca3af' : '#9ca3af',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'color 0.2s',
        },
        fieldError: {
            color: isDark ? '#fca5a5' : '#dc2626',
            fontSize: '0.875rem',
            marginTop: '0.25rem',
        },
        rememberRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        checkboxLabel: {
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem',
            color: isDark ? '#d1d5db' : '#374151',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
        },
        checkbox: {
            marginRight: '0.5rem',
            cursor: 'pointer',
            accentColor: isDark ? '#10b981' : '#10b981',
        },
        forgotLink: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: isDark ? '#00d4ff' : '#8b5cf6',
            textDecoration: 'none',
            transition: 'color 0.2s',
        },
        button: {
            width: '100%',
            padding: '0.75rem 1rem',
            background: isDark
                ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(16, 185, 129, 0.3), 0 2px 4px -1px rgba(52, 211, 153, 0.2)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        buttonDisabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
        spinner: {
            marginRight: '0.75rem',
            animation: 'spin 1s linear infinite',
        },
        signupText: {
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            transition: 'color 0.3s ease',
        },
        signupLink: {
            fontWeight: 500,
            color: isDark ? '#00d4ff' : '#8b5cf6',
            textDecoration: 'none',
            transition: 'color 0.2s',
        },
        footer: {
            marginTop: '2rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: isDark ? '#6b7280' : '#6b7280',
            transition: 'color 0.3s ease',
        },
    });

    const styles = getStyles();

    return (
        <>
            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @media (min-width: 1024px) {
          .left-panel { display: flex !important; }
        }
        
        input:focus {
          outline: none;
          border-color: ${isDark ? '#10b981' : '#10b981'} !important;
          box-shadow: 0 0 0 3px ${isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)'} !important;
          background-color: ${isDark ? '#333' : 'white'} !important;
        }
        
        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: ${isDark
                    ? '0 10px 15px -3px rgba(16, 185, 129, 0.4), 0 4px 6px -2px rgba(52, 211, 153, 0.3)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                } !important;
        }
        
        button:active:not(:disabled) {
          transform: translateY(0);
        }

        .theme-toggle:hover {
          transform: scale(1.05);
        }

        a:hover {
          opacity: 0.8;
        }
      `}</style>

            <div style={styles.container}>
                {/* Left Panel */}
                <div className="left-panel" style={styles.leftPanel}>
                    <div style={styles.leftContent}>
                        <h1 style={styles.title}>ADMIN Transport Portal</h1>
                        <p style={styles.subtitle}>Manage your sales, drivers, and operations with ease.</p>
                        <ul style={styles.featureList}>
                            <li style={styles.featureItem}>
                                <div style={styles.featureDot}></div>
                                Track sales and expenses in real-time
                            </li>
                            <li style={styles.featureItem}>
                                <div style={styles.featureDot}></div>
                                Manage drivers and vehicles efficiently
                            </li>
                            <li style={styles.featureItem}>
                                <div style={styles.featureDot}></div>
                                Generate comprehensive reports
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Right Panel */}
                <div style={styles.rightPanel}>
                    <div style={styles.formContainer}>
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            style={styles.themeToggle}
                            className="theme-toggle"
                            type="button"
                        >
                            <span style={styles.themeIcon}>{isDark ? '☀️' : '🌙'}</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#fff' : '#374151' }}>
                                {isDark ? 'Light' : 'Dark'}
                            </span>
                        </button>

                        <div style={styles.card}>
                            {/* Mobile Logo */}
                            <div style={styles.logoMobile} className="lg-hidden">
                                <h1 style={styles.logoText}>ADMIN Transport Portal</h1>
                            </div>

                            <div style={styles.header}>
                                <h2 style={styles.heading}></h2>
                                <p style={styles.subheading}>Please sign in to your account</p>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div style={styles.errorBox}>
                                    <p style={styles.errorText}>{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} style={styles.form}>
                                {/* Email Field */}
                                <div style={styles.formGroup}>
                                    <label htmlFor="email" style={styles.label}>
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        style={{
                                            ...styles.input,
                                            ...(errors.email ? styles.inputError : {}),
                                        }}
                                        placeholder="you@example.com"
                                    />
                                    {errors.email && (
                                        <p style={styles.fieldError}>{errors.email}</p>
                                    )}
                                </div>

                                {/* Password Field */}
                                <div style={styles.formGroup}>
                                    <label htmlFor="password" style={styles.label}>
                                        Password
                                    </label>
                                    <div style={styles.inputWrapper}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            style={{
                                                ...styles.input,
                                                ...(errors.password ? styles.inputError : {}),
                                            }}
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={styles.passwordToggle}
                                        >
                                            {showPassword ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p style={styles.fieldError}>{errors.password}</p>
                                    )}
                                </div>

                                {/* Remember Me & Forgot Password */}
                                <div style={styles.rememberRow}>
                                    <label style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            style={styles.checkbox}
                                        />
                                        Remember me
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    style={{
                                        ...styles.button,
                                        ...(isLoading ? styles.buttonDisabled : {}),
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <svg
                                                style={styles.spinner}
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                    opacity="0.25"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    opacity="0.75"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                />
                                            </svg>
                                            Signing in...
                                        </>
                                    ) : (
                                        'Sign in'
                                    )}
                                </button>
                            </form>
                        </div>



                        {/* Footer */}
                        <p style={styles.footer}>
                            © 2025 Kehillah Global. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoginForm;
